import React, { useState, useEffect } from 'react';
import { Field } from 'formik';
import { toast } from 'react-toastify';
import { useFileUpload } from '../../hooks/useFileUpload';
import Box from 'apollo-react/components/Box';
import Typography from 'apollo-react/components/Typography';
import IconButton from 'apollo-react/components/IconButton';
import CircularProgress from 'apollo-react/components/CircularProgress';
import Trash from 'apollo-react-icons/Trash';
import Image from 'apollo-react-icons/Image';
import FormHelperText from 'apollo-react/components/FormHelperText';

/**
 * Formik Image Upload Component with Preview
 * Specialized component for image uploads that shows a preview after upload
 * 
 * Features:
 * - Single image upload with preview
 * - Custom styled upload button (dashed border, subtle background)
 * - Replaces upload button with image preview after upload
 * - Delete functionality to remove uploaded image
 * - Integrated animated circular progress indicator with percentage
 * - Integration with existing file upload hooks
 * - Uses sx prop for styling (MUI v5 standard)
 * 
 * @param {string} name - Formik field name
 * @param {string} label - Label for the upload field
 * @param {string} helperText - Helper text shown below the upload area
 * @param {string} accept - Accepted file types (default: images only)
 * @param {boolean} required - Whether the field is required
 * @param {boolean} isAdminUpload - Whether to use admin upload endpoint
 * @param {number} maxSizeMB - Maximum file size in MB (default: 5MB for images)
 */
export const FormikImageUploadWithPreview = ({
  name,
  label = 'Upload Image',
  helperText,
  accept = '.jpg,.jpeg,.png,.gif',
  required = false,
  isAdminUpload = false,
  maxSizeMB = 5,
  ...rest
}) => {
  const { uploadFile, uploading, validateFile, uploadProgress } = useFileUpload(isAdminUpload, name);
  const [localPreviewUrl, setLocalPreviewUrl] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    return () => {
      if (localPreviewUrl) {
        setLocalPreviewUrl(null);
      }
    };
  }, [localPreviewUrl]);

  useEffect(() => {
    const preventDefaults = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    window.addEventListener('dragover', preventDefaults);
    window.addEventListener('drop', preventDefaults);

    return () => {
      window.removeEventListener('dragover', preventDefaults);
      window.removeEventListener('drop', preventDefaults);
    };
  }, []);

  return (
    <Box sx={{ width: '100%', marginTop: 2, marginBottom: 2 }}>
      <Field name={name}>
        {({ field, form: { errors, touched, setFieldValue } }) => {
          const currentFiles = field.value || [];
          const filesArray = Array.isArray(currentFiles) ? currentFiles : [currentFiles].filter(Boolean);
          const uploadedImage = filesArray.length > 0 ? filesArray[0] : null;
          
          const displayPreviewUrl = localPreviewUrl || (uploadedImage?.preview);

          const processFile = async (file) => {
            if (!file) return;

            // Image-specific validation
            const imageExtensions = accept.split(',').map(ext => ext.trim().toLowerCase());
            const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
            
            if (!imageExtensions.includes(fileExtension)) {
              toast.error(`Invalid file type. Please upload an image file (${accept})`, {
                position: 'top-center',
              });
              return;
            }

            // Check if it's actually an image by MIME type
            if (!file.type.startsWith('image/')) {
              toast.error('Please upload a valid image file (JPEG, PNG, or GIF)', {
                position: 'top-center',
              });
              return;
            }

            const validation = validateFile(file, maxSizeMB);
            if (!validation.isValid) {
              toast.error(validation.error, {
                position: 'top-center',
              });
              return;
            }

            validation.warnings.forEach((warning) => {
              console.warn('File upload warning:', warning);
            });

            try {
              const uploadedFile = await uploadFile(file, null, true);

              if (uploadedFile) {
                const reader = new FileReader();
                reader.onloadend = () => {
                  const previewUrl = reader.result;
                  setLocalPreviewUrl(previewUrl);
                  
                  const fileWithPreview = {
                    ...uploadedFile,
                    preview: previewUrl
                  };
                  setFieldValue(name, [fileWithPreview]);
                };
                reader.onerror = () => {
                  toast.error('Failed to read image file. Please try again.', {
                    position: 'top-center',
                  });
                };
                reader.readAsDataURL(file);
              }
            } catch (error) {
              console.error('Upload failed:', error);
              const errorMessage = error.response?.data?.message || error.message || 'Upload failed';
              toast.error(errorMessage, {
                position: 'top-center',
              });
            }
          };

          const handleFileSelect = async (event) => {
            const file = event.target.files?.[0];
            await processFile(file);

            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
          };

          const handleDragEnter = (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!uploading) {
              setIsDragging(true);
            }
          };

          const handleDragLeave = (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.currentTarget === e.target) {
              setIsDragging(false);
            }
          };

          const handleDragOver = (e) => {
            e.preventDefault();
            e.stopPropagation();
          };

          const handleDrop = async (e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);

            if (uploading || uploadedImage) return;

            const files = e.dataTransfer?.files;
            if (files && files.length > 0) {
              const file = files[0];
              
              const acceptedExtensions = accept.split(',').map(ext => ext.trim().toLowerCase());
              const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
              
              if (!acceptedExtensions.includes(fileExtension)) {
                toast.error(`Please upload an image file (${accept})`, {
                  position: 'top-center',
                });
                return;
              }

              await processFile(file);
            }
          };

          const handleDelete = () => {
            if (displayPreviewUrl && displayPreviewUrl.startsWith('blob:')) {
              window.URL.revokeObjectURL(displayPreviewUrl);
            }
            setFieldValue(name, []);
            setLocalPreviewUrl(null);
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
          };

          const handleUploadClick = () => {
            if (!uploading && fileInputRef.current) {
              fileInputRef.current.click();
            }
          };

          const hasError = !!(touched[name] && errors[name]);

          return (
            <>
              {label && (
                <Typography variant="body2" sx={{ marginBottom: 1, display: 'block' }}>
                  {label}
                  {required && ' *'}
                </Typography>
              )}

              {uploadedImage && displayPreviewUrl ? (
                <Box
                  sx={{
                    width: '100%',
                    minHeight: 200,
                    border: '2px solid #CCCCCC',
                    borderRadius: 1,
                    backgroundColor: '#F5F5F5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    component="img"
                    src={displayPreviewUrl}
                    alt={uploadedImage.name || 'Uploaded preview'}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: 400,
                      objectFit: 'contain',
                    }}
                  />
                  <IconButton
                    onClick={handleDelete}
                    size="small"
                    aria-label="Delete image"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: '#D32F2F',
                      color: '#FFFFFF',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: '#C62828',
                        color: '#FFFFFF',
                        transform: 'scale(1.05)',
                        boxShadow: '0 2px 8px rgba(211, 47, 47, 0.4)',
                      },
                    }}
                  >
                    <Trash />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  onClick={handleUploadClick}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleUploadClick();
                    }
                  }}
                  sx={(theme) => ({
                    width: '100%',
                    minHeight: 200,
                    border: '2px dashed #D0D5DD',
                    borderRadius: 1,
                    backgroundColor: '#FAFAFA',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    padding: 2,
                    opacity: uploading ? 0.6 : 1,
                    ...(isDragging && {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: '#E8F4FD',
                      borderWidth: '3px',
                    }),
                    ...(hasError && {
                      borderColor: theme.palette.error.main,
                    }),
                    '&:hover': {
                      backgroundColor: uploading ? '#FAFAFA' : '#F0F0F0',
                      borderColor: uploading ? '#D0D5DD' : '#98A2B3',
                    },
                    '&:focus': {
                      outline: 'none',
                      borderColor: theme.palette.primary.main,
                      backgroundColor: '#F5F5F5',
                      boxShadow: `0 0 0 3px ${theme.palette.primary.main}33`,
                    },
                  })}
                >
                  {uploading ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: '#666666', textAlign: 'center', fontWeight: 500 }}
                      >
                        Uploading...
                      </Typography>
                      <CircularProgress 
                        value={uploadProgress || 0} 
                        statusText={`${Math.round(uploadProgress || 0)}%`}
                      />
                    </Box>
                  ) : (
                    <>
                      <Image style={{ fontSize: 48, color: '#666666', marginBottom: 8 }} />
                      <Typography variant="body2" sx={{ color: '#666666', textAlign: 'center' }}>
                        {isDragging ? 'Drop image here' : 'Click to upload or drag and drop'}
                      </Typography>
                      {helperText && !hasError && !isDragging && (
                        <Typography
                          variant="caption"
                          sx={{ color: '#666666', textAlign: 'center', marginTop: 0.5 }}
                        >
                          {helperText}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              )}

              <Box
                component="input"
                ref={fileInputRef}
                type="file"
                accept={accept}
                onChange={handleFileSelect}
                disabled={uploading}
                sx={{ display: 'none' }}
                {...rest}
              />

              {hasError && (
                <FormHelperText error>
                  {errors[name]}
                </FormHelperText>
              )}
            </>
          );
        }}
      </Field>
    </Box>
  );
};

export default FormikImageUploadWithPreview;

